import ScrollModal from "@/components/common/scroll-modal";
import TextField from "@/components/common/text-field";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import {
  Button,
  Heading,
  Icon,
  Input,
  Radiobutton,
  GroupTextField,
  GroupTextFieldRow,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import TextFieldID from "@/pages/digital-files/entity/components/TextFieldID";
import SwitchField from "@/workflow/components/configurations/form/components/SwitchField";
import InfoMessage from "@/workflow/components/configurations/form/components/InfoMessage";
import type { EntityField } from "../../types";
import { useConfigModal } from "../hooks/useConfigModal";
import { DataCenterField } from "../components/DataCenterField";

interface EmailFieldConfigModalProps {
  isOpen: boolean;
  isCreateMode?: boolean;
  formData?: EntityField;
  onSave: (data: EntityField) => void;
  onClose: () => void;
}

export const EmailFieldConfigModal = ({
  isOpen,
  isCreateMode = true,
  formData,
  onSave,
  onClose,
}: EmailFieldConfigModalProps) => {
  const [disallowedDomainsNew, setDisallowedDomainsNew] = useState("");
  const [allowedDomainsNew, setAllowedDomainsNew] = useState("");
  const [localListNewItem, setLocalListNewItem] = useState("");

  // Validar que sea un dominio válido (ejemplo: @ejemplo.com o @ejemplo.com.sv)
  const isValidDomain = (value: string): boolean => {
    if (!value.trim()) return false;
    // Debe empezar con @ seguido de un dominio válido
    const domainRegex = /^@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
    return domainRegex.test(value.trim());
  };
  const {
    fieldKey,
    fieldName,
    fieldNameError,
    defaultValue,
    localListItems,
    hasDropdown,
    buttonPosition,
    valuesSource,
    expandSelectedRecord,
    placeholder,
    allowedDomains,
    noAllowedDomains,
    valuesColumnName,
    valuesTableName,
    setFieldKey,
    setFieldName,
    setDefaultValue,
    setLocalListItems,
    setHasDropdown,
    setButtonPosition,
    setValuesSource,
    setExpandSelectedRecord,
    setPlaceholder,
    setAllowedDomains,
    setNoAllowedDomains,
    setDataCenterValue,
    getConfigData,
    isValid,
    isNew,
  } = useConfigModal("email", formData);

  const handleSave = () => {
    const values = getConfigData();
    onSave(values);
    onClose();
  };

  const handleAddLocalItem = () => {
    const v = localListNewItem.trim();
    if (!v) return;
    setLocalListItems((prev) => [...prev, v]);
    setLocalListNewItem("");
  };

  const handleRemoveLocalItem = (index: number) => {
    setLocalListItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddAllowedDomain = () => {
    const v = allowedDomainsNew.trim();
    if (!v || !isValidDomain(v)) return;
    if (allowedDomains.includes(v)) return;
    setAllowedDomains((prev) => [...prev, v]);
    setAllowedDomainsNew("");
  };

  const handleRemoveAllowedDomain = (index: number) => {
    setAllowedDomains((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddDisallowedDomain = () => {
    const v = disallowedDomainsNew.trim();
    if (!v || !isValidDomain(v)) return;
    if (noAllowedDomains.includes(v)) return;
    setNoAllowedDomains((prev) => [...prev, v]);
    setDisallowedDomainsNew("");
  };

  const handleRemoveDisallowedDomain = (index: number) => {
    setNoAllowedDomains((prev) => prev.filter((_, i) => i !== index));
  };

  const isDefaultValueInvalid = useMemo(() => {
    if (!defaultValue || !defaultValue.trim()) return false;

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(defaultValue.trim())) return true;

    // Extraer el dominio del email (incluye @)
    const emailDomain = defaultValue.trim().match(/@[^\s@]+$/)?.[0];
    if (!emailDomain) return true;

    // Validar contra dominios permitidos si existen
    if (allowedDomains.length > 0) {
      if (!allowedDomains.includes(emailDomain)) return true;
    }

    // Validar contra dominios no permitidos si existen
    if (noAllowedDomains.length > 0) {
      if (noAllowedDomains.includes(emailDomain)) return true;
    }

    return false;
  }, [defaultValue, allowedDomains, noAllowedDomains]);

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col gap-0 text-size-body-sm"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <Heading variant="h5">Correo electrónico</Heading>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={onClose}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="px-8 py-3">
          <div className="flex gap-3">
            <Button variant="tertiary" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="default"
              className="flex-1"
              disabled={!isValid || isDefaultValueInvalid}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </div>
        </div>
      }
    >
      <div className="p-8 flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Detalles
          </Heading>

          <TextFieldID
            label="Nombre del campo"
            required
            value={fieldName}
            onChange={setFieldName}
            keyValue={fieldKey}
            onKeyChange={setFieldKey}
            canChangeKey={isCreateMode || isNew}
            error={fieldNameError}
          />
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Opciones
          </Heading>

          <SwitchField
            id="email-field-dropdown"
            label="¿Agregar botón dropdown al campo?"
            required
            checked={hasDropdown}
            onChange={setHasDropdown}
            isTrue="Sí"
            isFalse="No"
          />

          {hasDropdown && (
            <>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-body-loud text-sm leading-2xl">
                  Posición del botón{" "}
                  <span className="text-sm text-danger">*</span>
                </label>
                <DropdownList
                  options={[
                    { value: "left", label: "Izquierda" },
                    { value: "right", label: "Derecha" },
                  ]}
                  placeholder="Seleccionar"
                  selectedValue={{
                    value: buttonPosition,
                    label: buttonPosition === "left" ? "Izquierda" : "Derecha",
                  }}
                  toggleSelection={(item) => {
                    if (item.value === "left" || item.value === "right") {
                      setButtonPosition(item.value);
                    }
                  }}
                  widthClassName="w-full"
                  scrollClassName="h-auto"
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="font-semibold text-body-loud text-sm leading-2xl">
                  ¿De donde provienen los valores?{" "}
                  <span className="text-sm text-danger">*</span>
                </div>
                <div className="flex flex-col gap-2 mt-1">
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="email-field-source-database"
                      name="email-field-source"
                      value="database"
                      checked={valuesSource === "database"}
                      onChange={() => setValuesSource("database")}
                    />
                    <label
                      htmlFor="email-field-source-database"
                      className="text-body text-sm"
                    >
                      Desde centro de datos
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="email-field-source-manual"
                      name="email-field-source"
                      value="manual"
                      checked={valuesSource === "manual"}
                      onChange={() => setValuesSource("manual")}
                    />
                    <label
                      htmlFor="email-field-source-manual"
                      className="text-body text-sm"
                    >
                      Locales
                    </label>
                  </div>
                </div>
              </div>

              {valuesSource === "manual" && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-end group">
                    <div className="w-full">
                      <TextField
                        placeholder="Añade un nuevo item"
                        className="rounded-tr-none rounded-br-none border-r-0"
                        label="Lista local"
                        required
                        value={localListNewItem}
                        onChange={(e) => setLocalListNewItem(e.target.value)}
                      />
                    </div>
                    <Button
                      className="h-11 rounded-bl-none rounded-tl-none border-l-0 group-hover:border-neutral-loud group-focus-within:border-contextual-action! group-hover:bg-neutral-object-accent-silent group-focus-within:bg-neutral-object-accent-quiet!"
                      variant="quiet"
                      type="button"
                      onClick={handleAddLocalItem}
                      aria-label="Agregar item"
                    >
                      <Icon name="plus" type="far" />
                    </Button>
                  </div>

                  <InfoMessage>
                    El primer valor de la lista será el pre-seleccionado por
                    default
                  </InfoMessage>

                  {localListItems.length > 0 && (
                    <div
                      role="region"
                      className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1"
                    >
                      <div className="flex flex-col">
                        {localListItems.map((item, index) => (
                          <div
                            key={`${item}-${index}`}
                            className="px-3 py-2 flex items-center gap-3"
                          >
                            <div className="flex-1 text-body text-sm truncate">
                              {item}
                            </div>
                            <Button
                              variant="quiet"
                              size="icon"
                              type="button"
                              aria-label="Eliminar"
                              onClick={() => handleRemoveLocalItem(index)}
                              className="w-8 h-8"
                            >
                              <Icon type="far" name="times" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {valuesSource === "database" && (
                <DataCenterField
                  required
                  fieldKey="valueDatacenter"
                  selectedTable={valuesTableName}
                  selectedColumn={valuesColumnName}
                  onChange={setDataCenterValue}
                />
              )}
              {valuesSource === "database" && (
                <SwitchField
                  id="email-field-expand-selected"
                  label="Expandir registro seleccionado"
                  required
                  checked={expandSelectedRecord}
                  onChange={setExpandSelectedRecord}
                  isTrue="Sí, expandir"
                  isFalse="No, generar una sola variable (array)"
                />
              )}
            </>
          )}
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Configuración
          </Heading>

          <TextField
            label="Placeholder del campo"
            required
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            placeholder="Escribe un placeholder para el campo"
          />

          <div className="flex flex-col gap-2">
            <div>
              <div className="flex items-end group">
                <div className="w-full">
                  <TextField
                    placeholder="@ejemplo.com.sv"
                    className="rounded-tr-none rounded-br-none border-r-0"
                    label="Dominios permitidos"
                    required
                    value={allowedDomainsNew}
                    onChange={(e) => setAllowedDomainsNew(e.target.value)}
                  />
                </div>
                <Button
                  className="h-11 rounded-bl-none rounded-tl-none disabled:border-neutral group-hover:border-neutral-loud group-focus-within:border-contextual-action! group-hover:bg-neutral-object-accent-silent group-focus-within:bg-neutral-object-accent-quiet!"
                  variant="quiet"
                  type="button"
                  onClick={handleAddAllowedDomain}
                  disabled={!isValidDomain(allowedDomainsNew)}
                  aria-label="Agregar dominio permitido"
                >
                  <Icon name="plus" type="far" />
                </Button>
              </div>

              <InfoMessage className="mt-1">
                Si ninguno es añadido, todos serán permitidos
              </InfoMessage>
            </div>

            {allowedDomains.length > 0 && (
              <div className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1">
                <div className="flex flex-col">
                  {allowedDomains.map((domain, index) => (
                    <div
                      key={`${domain}-${index}`}
                      className="px-3 py-2 flex items-center gap-3"
                    >
                      <div className="flex-1 text-body text-sm truncate">
                        {domain}
                      </div>
                      <Button
                        variant="quiet"
                        size="icon"
                        type="button"
                        aria-label="Eliminar"
                        onClick={() => handleRemoveAllowedDomain(index)}
                        className="w-8 h-8"
                      >
                        <Icon type="far" name="times" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div>
              <div className="flex items-end group">
                <div className="w-full">
                  <TextField
                    placeholder="@ejemplo.com.sv"
                    className="rounded-tr-none rounded-br-none border-r-0"
                    label="Dominios no permitidos"
                    required
                    value={disallowedDomainsNew}
                    onChange={(e) => setDisallowedDomainsNew(e.target.value)}
                  />
                </div>
                <Button
                  className="h-11 rounded-bl-none rounded-tl-none disabled:border-neutral group-hover:border-neutral-loud group-focus-within:border-contextual-action! group-hover:bg-neutral-object-accent-silent group-focus-within:bg-neutral-object-accent-quiet!"
                  variant="quiet"
                  type="button"
                  onClick={handleAddDisallowedDomain}
                  disabled={!isValidDomain(disallowedDomainsNew)}
                  aria-label="Agregar dominio no permitido"
                >
                  <Icon name="plus" type="far" />
                </Button>
              </div>

              <InfoMessage className="mt-1">
                Si ninguno es añadido, todos serán permitidos
              </InfoMessage>
            </div>

            {noAllowedDomains.length > 0 && (
              <div className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1">
                <div className="flex flex-col">
                  {noAllowedDomains.map((domain, index) => (
                    <div
                      key={`${domain}-${index}`}
                      className="px-3 py-2 flex items-center gap-3"
                    >
                      <div className="flex-1 text-body text-sm truncate">
                        {domain}
                      </div>
                      <Button
                        variant="quiet"
                        size="icon"
                        type="button"
                        aria-label="Eliminar"
                        onClick={() => handleRemoveDisallowedDomain(index)}
                        className="w-8 h-8"
                      >
                        <Icon type="far" name="times" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <GroupTextField
              label="Valor por defecto"
              labelHelperText="Opcional"
              error={
                isDefaultValueInvalid
                  ? allowedDomains.length > 0
                    ? "El email debe tener un formato válido y usar un dominio permitido."
                    : noAllowedDomains.length > 0
                      ? "El email debe tener un formato válido y no usar un dominio restringido."
                      : "El email debe tener un formato válido."
                  : undefined
              }
            >
              <GroupTextFieldRow className="items-center">
                <Input
                  value={defaultValue}
                  onChange={(e) => setDefaultValue(e.target.value)}
                  placeholder="Escribe un valor por defecto"
                  className="w-full"
                  type="email"
                />
              </GroupTextFieldRow>
            </GroupTextField>

            <InfoMessage className="mt-1">
              Si está vacío no se aplicará ningún valor por defecto.
            </InfoMessage>
          </div>
        </div>
      </div>
    </ScrollModal>
  );
};
